import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import { useNavigate, useParams } from 'react-router-dom';
import classnames from 'classnames';
import { Alert, CardBody, CardDeck, CardSubtitle, CardText, CardTitle } from 'reactstrap';
import Sidebar from '../../components/manager/assessment/sidebar';
import KeyBehaviourCoverage from '../../components/manager/assessment/key-behaviour-coverage';
import WizardSummary from '../../components/manager/wizard/WizardSummary';
import QuestionsOrder from '../../components/manager/assessment/questions-order';
import Settings from '../../components/manager/assessment/settings';
import PersonalizeForm from '../../components/manager/assessment/personalize-form';
import ErrorMessage from '../../components/errormessage';
import Modal from 'react-modal';
import Snackbar from '../../components/Snackbar';
import { isNameUntitled, querystring } from '../../utils/helpers';
import ThemedLinkButton from '../../components/themed/ThemedLinkButton';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import pluralize from 'pluralize';
import { ASSESSMENT_INSIGHT_WIZARD_STEPS, ASSESSMENT_WIZARD_STEPS, STATUS } from '../../utils/constants';
import '../assessment/AssessmentCreate.scss';
import ChangeName from '../../components/manager/wizard/change-name';
import PreviewQuestion from '../../components/manager/assessment/preview-question';
import SaveChanges from '../../components/manager/wizard/save-changes';
import WizardHeader from '../../components/manager/wizard/WizardHeader';
import LockIcon from '@mui/icons-material/Lock';
import AlertWithIcon from '../../components/Alert';
import { Heading1, TextTiny, ThemedCard } from '@awarego/awarego-components';
import AddScenariosModal from '../../components/manager/assessment/AddScenariosModal';
import StartScreen from '../../components/manager/assessment/start-screen';
import WizardStepHeader from '../../components/manager/wizard/WizardStepHeader';
import ChangeDuration from '../../components/manager/wizard/change-duration';
import { useAssessmentType } from '../assessment/useAssessmentType';
import uniq from 'lodash/uniq';

function AssessmentInsightCreate({ companyId }) {
    const {
        assessmentInsightStore,
        authStore,
        commonStore,
        store,
        brandingStore,
    } = useStores();
    const { parentRoute, name, title } = useAssessmentType();
    const [questionsModal, setQuestionsModal] = useState(false);
    const [previewQuestionsModal, setPreviewQuestionsModal] = useState(false);
    const [previewedQuestion, setPreviewedQuestion] = useState(false);
    const [createdAssessment, setCreatedAssessment] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageShown, setMessageShown] = useState(false);
    const [messageType, setMessageType] = useState(null);
    const [nameChangeModal, setNameChangeModal] = useState(false);
    const [durationOverrideModal, setDurationOverrideModal] = useState(false);
    const [isDraft, setIsDraft] = useState(false);
    const [redirectLink, setRedirectLink] = useState(null);
    const [hasMoreQuestions, setHasMoreQuestions] = useState(false);
    const [saveChangesModal, setSaveChangesModal] = useState(false);
    const [area, setArea] = useState('all');
    const [behaviour, setBehaviour] = useState('all');

    const navigate = useNavigate();
    const { id: editId, fromId: copyFrom } = useParams();
    const back = querystring('back');

    const {
        assessment,
        loadingCompanyData,
        step,
        steps,
        allowedSteps,
        questions,
        error,
        templates,
        selectedTemplates,
        maxByAreaAndBehaviourForAllQuestions,
        personalizeForm,
        threatAreaDefinitions,
        summaryOutput,
        emailsForm,
        saveButtonAvailable
    } = assessmentInsightStore;

    const { timeZone } = authStore;

    const {
        currentCompany,
        havePartnerSubscription,
        ssoEnabled,
        languages,
        enabledLanguages
    } = store;
    const { supportEmail, myDomain } = brandingStore;
    const editOngoing = assessment && assessment.status === STATUS.ACTIVE && assessment._rawData.usersTotal > 0;
    const generalOngoing = assessment && assessment.status === STATUS.ACTIVE;

    const loadData = useCallback(async () => {
        await assessmentInsightStore.prepareAssessmentInsightWizard(
            companyId,
            copyFrom,
            editId,
            enabledLanguages
        );
    }, [
        assessmentInsightStore,
        companyId,
        copyFrom,
        editId,
        enabledLanguages
    ]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const toggleQuestionSelected = (questionId) => {
        if (!assessment.templateCustomized) {
            assessmentInsightStore.setTemplateAsCustom();
        }
        assessment.toggleQuestionSelected(questionId);
    };

    const createOptionsArray = (languages, enabledOnly) => {
        let optionsArray = [];
        if(enabledOnly) {
            languages = languages.filter((lang) => enabledLanguages?.includes(lang.code)) || [];
        }
        for (let i = 0; i < languages.length; i++) {
            optionsArray.push({
                key: languages[i].code,
                value: languages[i].code,
                name: languages[i].english_name,
                img: languages[i].flag_image,
            });
        }
        return optionsArray;
    };

    const openNameChangeModal = (isDraft, redirectLink) => {
        assessment.setName('');
        setSaveChangesModal(false);
        setNameChangeModal(true);
        setIsDraft(isDraft);
        setRedirectLink(redirectLink);
    };

    const hideNameChangeModal = () => {
        setNameChangeModal(false);
    };

    const openDurationOverrideModal = () => {
        setDurationOverrideModal(true);
    };

    const hideDurationOverrideModal = () => {
        setDurationOverrideModal(false);
    };

    const onChangeEmailsEnabled = () => {
        assessment.setEmailsEnabled()
    };

    const onChangeBannerImageSrc = (src) => {
        assessment.setBannerImageSrc(src);
    }

    const onChangeBannerLinkURL = (url) => {
        assessment.setBannerLinkURL(url);
    }

    const onChangeFinishUrl = (url) => {
        assessment.setFinishUrl(url);
    }

    const onChangeCustomizationEnabled = () => {
        assessment.setCustomizationEnabled()
    };

    const redirectToUpgrade = () => {
        commonStore.showConfirm(
            `Do you want to save this ${name} as draft?`,
            'Save as draft & continue',
            'Before you upgrade your subscription',
            () => {
                initiateSavingProgress(null, '/subscriptions/upgrade');
            }
        );
    };

    const initiateSavingProgress = (redirectTo) => {
        if (isNameUntitled(assessment.name, 'assessment_insight')) {
            return openNameChangeModal(true, redirectTo);
        }
        return saveChanges(redirectTo);
    }

    const saveChanges = async (redirectTo = parentRoute) => {
        const isDraft = assessment.active === false;
        const isNew = assessment.id === null;
        let result = await assessmentInsightStore.assessmentSaver(companyId, {
            personalizeData:
                assessment.personalizeEnabled && personalizeForm
                    ? personalizeForm.values()
                    : undefined,
            emailsToNotify: assessment.emails_enabled && emailsForm
                ? uniq(emailsForm.values().users.filter(
                    (user) => user.email
                ))
                : undefined,
        });

        if (result) {
            !isDraft
                ? commonStore.success(`${title} ${assessment.name} updated!`)
                : isNew
                ? commonStore.success(`${title} ${assessment.name} saved as draft!`)
                : commonStore.success(`${title} ${assessment.name} draft updated!`);
            navigate(back || redirectTo);
        } else {
            if (assessmentInsightStore.error) {
                showSnackBar(assessmentInsightStore.error, 'error');
            }
        }
    };

    const openSaveChangesModal = () => {
        setSaveChangesModal(true);
    };

    const hideSaveChangesModal = () => {
        setSaveChangesModal(false);
    };

    const reorderList = (sourceIndex, targetIndex) => {
        assessment.reorderQuestionsList(sourceIndex, targetIndex);
    };

    const onChangeAuthentication = (value) => {
        assessment.setAuthentication(value);
    };

    const startAssessment = async () => {
        let result = await assessmentInsightStore.assessmentSaver(companyId, {
            personalizeData: assessment.personalizeEnabled
                ? personalizeForm.values()
                : undefined,
            emailsToNotify: assessment.emails_enabled && emailsForm
            ? uniq(emailsForm.values().users.filter(
                (user) => user.email
                ))
            : undefined,
        }, true);
        if (result) {
            assessmentInsightStore.setStep(ASSESSMENT_INSIGHT_WIZARD_STEPS.START);
            setCreatedAssessment(result);
        } else {
            if (assessmentInsightStore.error) {
                showSnackBar(assessmentInsightStore.error, 'error');
            }
        }
    };

    const stepChange = (step) => {
        assessmentInsightStore.setStep(step);
    };

    const onChangeName = (e) => {
        assessment.setName(e.target.value);
    };

    const onChangeDuration = (minutes) => {
        assessment.setDurationOverride(minutes * 60);
    };

    const getOverrideDurationInMinutes = () => {
        return (
            assessment &&
            assessment.overrideDuration &&
            Math.ceil(assessment.overrideDuration / 60)
        );
    };

    const onChangeDueDate = (value) => {
        assessment.setDueDate(value);
    };

    const selectTemplate = (template) => {
        assessmentInsightStore.setTemplate(template.id);
    };

    const nextStep = () => {
        if (assessmentInsightStore.step === ASSESSMENT_INSIGHT_WIZARD_STEPS.PERSONALIZE) {
            assessment.personalizedKeys.map((pk) =>
                window.localStorage.setItem(
                    `${pk}-${store.currentCompany.company_id}`,
                    personalizeForm.$(pk).value
                )
            );
        }

        if(assessmentInsightStore.step === ASSESSMENT_INSIGHT_WIZARD_STEPS.SETTINGS) {
            emailsForm?.validate()
            personalizeForm?.validate()
            const wizardValid =
                assessmentInsightStore.goalValid &&
                assessmentInsightStore.contentValid &&
                assessmentInsightStore.personalizeValid &&
                assessmentInsightStore.settingsValid;
            if(!wizardValid) {
                return showSnackBar('Please ensure all settings are valid', 'error');
            }
        }

        if (assessmentInsightStore.step === ASSESSMENT_INSIGHT_WIZARD_STEPS.FINALIZE)
            if (
                assessment.name === `Untitled ${title}` ||
                assessment.name === '' ||
                assessment.name == null
            ) {
                openNameChangeModal(false);
            } else {
                startAssessment();
            }
        else {
            const nextStep = assessmentInsightStore.nextStepNumber;
            assessmentInsightStore.setStep(nextStep);
        }
    };

    const prevStep = () => {
        let index = assessmentInsightStore.steps.findIndex(
            (x) => x.number === assessmentInsightStore.step
        );
        let prevStep = assessmentInsightStore.steps[index - 1]
            ? assessmentInsightStore.steps[index - 1].number
            : assessmentInsightStore.step - 1;

        assessmentInsightStore.setStep(prevStep);
    };

    const showQuestionsModal = () => {
        setQuestionsModal(true);
    };

    const hideQuestionsModal = () => {
        setQuestionsModal(false);
    };

    const showPreviewQuestionsModal = (questionId, type) => {
        setPreviewQuestionsModal(true);
        setPreviewedQuestion(questionId);
        setHasMoreQuestions(type);
    };

    const hidePreviewQuestionsModal = () => {
        setPreviewQuestionsModal(false);
    };

    const showSnackBar = (message, messageType = 'success') => {
        setMessage(message);
        setMessageShown(true);
        setMessageType(messageType);
    };

    const closeSnackbar = () => {
        setMessage(null);
        setMessageShown(false);
        setMessageType(null);
    };

    const getTemplateQuestionsStats = (template) => {
        if (!template) return null;

        const questionCount =
            template.questions.length + template.disabledQuestions.length;
        if (questionCount === 0) return null;

        return `${questionCount} questions · ${
            template.verboseDuration
        } ${pluralize('minute', template.verboseDuration)}`;
    };

    const getTemplateQuestionsAvailabilityElement = (template) => {
        if (!template) return null;
        if (
            !template.disabledQuestions ||
            template.disabledQuestions.length === 0
        ) {
            return null;
        }

        const text = `${template.disabledQuestions.length}/${
            template.questions.length + template.disabledQuestions.length
        } questions unavailable in your plan`;

        return (
            <TextTiny bold error>
                <LockIcon fontSize={'inherit'} /> {text}
            </TextTiny>
        );
    };

    const getReviewQuestionsAvailabilityElement = () => {
        const selectedQuestions = assessment.questionsSelected;

        if (!selectedQuestions) return null;

        const disabledQuestionsCount =
            assessment.unavailableQuestionsSelected &&
            assessment.unavailableQuestionsSelected.length;

        if (disabledQuestionsCount === 0) return null;

        const count = `${disabledQuestionsCount}/${selectedQuestions.length}`;

        return havePartnerSubscription ? (
            <AlertWithIcon severity="error">
                {count} selected questions are unavailable in your plan. Please{' '}
                contact <a href={`mailto:${supportEmail}`}>{supportEmail}</a> to
                upgrade or remove unavailable.
            </AlertWithIcon>
        ) : (
            <AlertWithIcon severity="error">
                {count} selected questions are unavailable in your plan. Please{' '}
                <u style={{ cursor: 'pointer' }} onClick={redirectToUpgrade}>
                    upgrade
                </u>
                {/* or <a href="#">remove unavailable</a> */}.
            </AlertWithIcon>
        );
    };

    return (
        <Fragment>
            <ErrorMessage error={error} />
            {loadingCompanyData || !assessment ? (
                <ThemedSpinner />
            ) : (
                <Fragment>
                    {assessment.status === STATUS.COMPLETED ? (
                        `You can't edit an already completed ${name}`
                    ) : currentCompany ? (
                        <div className={'training-wizard'}>
                            {step!== ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                            <WizardHeader
                                selectedTemplate={selectedTemplates}
                                isStartStep={
                                    step === ASSESSMENT_INSIGHT_WIZARD_STEPS.START
                                }
                                openSaveChangesModal={openSaveChangesModal}
                                item={assessment}
                                onChangeName={onChangeName}
                                itemString={name}
                                saveButtonAvailable={saveButtonAvailable}
                                initiateSavingProgress={initiateSavingProgress}
                                isNameUntitled={isNameUntitled(
                                    assessment.name,
                                    'assessment_insight'
                                )}
                            />
                            )}
                            {assessment.id !== null && step!== ASSESSMENT_INSIGHT_WIZARD_STEPS.START &&
                                editOngoing && (
                                    <div className="assessment-wizard-info-message">
                                        <span style={{ fontWeight: 700 }}>
                                            Edit assessment insight&nbsp;
                                        </span>{'– Some parts of the assessment can not be modified as employees have already received questions.'}
                                    </div>
                                )}
                            <div className={'training-wizard-body'}>
                                <Snackbar
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    autoHideDuration={15000}
                                    variant={messageType}
                                    message={message || ''}
                                    open={messageShown}
                                    onClose={closeSnackbar}
                                />
                                {step !== ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                                    <Sidebar
                                        steps={steps}
                                        activeStep={step}
                                        allowedSteps={allowedSteps}
                                        onStepChange={stepChange}
                                        warningSteps={[
                                            assessment.hasUnavailableQuestions
                                                ? ASSESSMENT_WIZARD_STEPS.CONTENT
                                                : null,

                                            ...assessmentInsightStore.allowedSteps.filter(
                                                (s) =>
                                                    !assessmentInsightStore.isStepValid(s) &&
                                                    assessmentInsightStore.visitedSteps.includes(s) &&
                                                    assessmentInsightStore.step !== s &&
                                                    s !== ASSESSMENT_WIZARD_STEPS.START &&
                                                    s !== ASSESSMENT_WIZARD_STEPS.GOAL &&
                                                    s !== ASSESSMENT_WIZARD_STEPS.FINALIZE
                                            ),
                                        ]}
                                        // extraInfo={assessment.id && (ongoing)}
                                    />
                                )}
                                <div
                                    className={classnames(
                                        'training-wizard-content',
                                        {
                                            'expand-content':
                                                step ===
                                                ASSESSMENT_INSIGHT_WIZARD_STEPS.START,
                                        }
                                    )}
                                >
                                    {step === ASSESSMENT_INSIGHT_WIZARD_STEPS.GOAL && (
                                        <Fragment>
                                            <WizardStepHeader
                                                title={
                                                    assessment.id !== null &&
                                                    editOngoing
                                                        ? `Selected template for your ${name}`
                                                        : `Set a template for your ${name}`
                                                }
                                                subtitle={
                                                    assessment.id !== null &&
                                                    editOngoing
                                                        ? `This is a template set for this ${name}.`
                                                        : 'Use one of our ready-made set of questions. You can always add additional questions.'
                                                }
                                            />
                                            <br />
                                            <CardDeck
                                                className={'training-templates'}
                                            >
                                                {(editOngoing
                                                    ? templates.filter((t) =>
                                                          selectedTemplates.includes(
                                                              t.id
                                                          )
                                                      )
                                                    : templates
                                                ).map((t) =>
                                                    t.hidden ||
                                                    !t.enabled ? null : (
                                                        <ThemedCard
                                                            key={t.id}
                                                            outline
                                                            disabled
                                                            {...{
                                                                selected:
                                                                    selectedTemplates.includes(
                                                                        t.id
                                                                    ),
                                                            }}
                                                            className={classnames(
                                                                'training-template',
                                                                {
                                                                    disabled:
                                                                        !t.enabled,
                                                                },
                                                                `training-template-${t.id}`
                                                            )}
                                                            onClick={() => {
                                                                if (
                                                                    t.enabled &&
                                                                    !editOngoing
                                                                )
                                                                    selectTemplate(
                                                                        t
                                                                    );
                                                            }}
                                                        >
                                                            <CardBody>
                                                                <CardTitle>
                                                                    <Heading1
                                                                        displayBlock
                                                                        mb={8}
                                                                    >
                                                                        {
                                                                            t.title
                                                                        }
                                                                    </Heading1>
                                                                </CardTitle>
                                                                <CardText>
                                                                    {
                                                                        t.description
                                                                    }
                                                                </CardText>
                                                                <CardSubtitle>
                                                                    <span>
                                                                        {getTemplateQuestionsStats(
                                                                            t
                                                                        )}
                                                                    </span>
                                                                </CardSubtitle>
                                                                {getTemplateQuestionsAvailabilityElement(
                                                                    t
                                                                )}
                                                            </CardBody>
                                                        </ThemedCard>
                                                    )
                                                )}
                                            </CardDeck>
                                        </Fragment>
                                    )}
                                    {step ===
                                        ASSESSMENT_INSIGHT_WIZARD_STEPS.CONTENT && (
                                        <Fragment>
                                            <WizardStepHeader
                                                title={'Review scenarios'}
                                                subtitle={
                                                    editOngoing
                                                        ? 'You cannot modify scenarios since at least somebody has already started the assessment.'
                                                        : 'You can edit or add more scenarios to this set of scenarios.'
                                                }
                                                summary={`${
                                                    assessment.questionsSelected
                                                        .length
                                                } ${pluralize(
                                                    'scenario',
                                                    assessment.questionsSelected
                                                        .length
                                                )} · ${
                                                    assessment.selectedQuestionsDuration
                                                } ${pluralize(
                                                    'minute',
                                                    assessment.selectedQuestionsDuration
                                                )}`}
                                                buttonText={
                                                    !editOngoing && 'Add scenarios'
                                                }
                                                onButtonClick={
                                                    showQuestionsModal
                                                }
                                            />

                                            {getReviewQuestionsAvailabilityElement()}

                                            <QuestionsOrder
                                                questions={
                                                    assessment.questionsSelected
                                                }
                                                onRemoveQuestion={
                                                    toggleQuestionSelected
                                                }
                                                onReorderList={reorderList}
                                                showPreviewQuestionsModal={
                                                    showPreviewQuestionsModal
                                                }
                                                ongoing={editOngoing}
                                            />
                                        </Fragment>
                                    )}
                                    {step ===
                                        ASSESSMENT_INSIGHT_WIZARD_STEPS.PERSONALIZE && (
                                        <Fragment>
                                            <WizardStepHeader
                                                title={
                                                    'Personalize to make it relatable'
                                                }
                                                subtitle={
                                                    `We use this information to make the ${name} scenarios more realistic an relatable.`
                                                }
                                            />
                                            <PersonalizeForm
                                                form={personalizeForm}
                                                editable={
                                                    assessment.personalizeEnabled
                                                }
                                                ongoing={editOngoing}
                                                personalizedKeys={
                                                    assessment.personalizedKeys
                                                }
                                            />
                                        </Fragment>
                                    )}

                                    {step ===
                                        ASSESSMENT_INSIGHT_WIZARD_STEPS.SETTINGS && (
                                            <Fragment>
                                                <WizardStepHeader
                                                    title={
                                                        'Assessment insight settings'
                                                    }
                                                    subtitle={
                                                        `Adjust your ${name} settings to fit your needs.`
                                                    }
                                                />
                                                <Settings
                                                    emails_enabled={assessment.emails_enabled}
                                                    onChangeEmailsEnabled={onChangeEmailsEnabled}
                                                    customization_enabled={assessment.customization_enabled}
                                                    onChangeCustomizationEnabled={onChangeCustomizationEnabled}
                                                    bannerImageSrc={assessment.bannerImageSrc}
                                                    bannerLinkURL={assessment.bannerLinkURL}
                                                    finishUrl={assessment.finishUrl}
                                                    onChangeBannerImageSrc={onChangeBannerImageSrc}
                                                    onChangeBannerLinkURL={onChangeBannerLinkURL}
                                                    onChangeFinishUrl={onChangeFinishUrl}
                                                    emailsForm={assessmentInsightStore.emailsForm}
                                                    authentication={assessment.authentication}
                                                    dueDate={assessment.due}
                                                    onChangeDueDate={onChangeDueDate}
                                                    onChangeAuthentication={onChangeAuthentication}
                                                    adminTimeZone={timeZone}
                                                    enabledLanguages={assessment.enabledLanguages}
                                                    createOptionsArray={createOptionsArray(languages)}
                                                    setEnabledLanguages={(value) => (assessmentInsightStore.setEnabledLanguages(value))}
                                                >
                                                </Settings>
                                            </Fragment>
                                        )}


                                    {step ===
                                        ASSESSMENT_INSIGHT_WIZARD_STEPS.FINALIZE && (
                                        <Fragment>
                                            <WizardStepHeader
                                                title={`Finalize ${title}`}
                                                subtitle={
                                                    generalOngoing
                                                        ? 'Check out the summary before applying changes.'
                                                        : `Check out the summary before you create the ${name}.`
                                                }
                                            />

                                            {getReviewQuestionsAvailabilityElement()}

                                            <WizardSummary
                                                summaryOutput={summaryOutput}
                                                assessment={assessment}
                                                ongoing={editOngoing}
                                                onChangeDuration={
                                                    openDurationOverrideModal
                                                }
                                                overrideDurationInMinutes={getOverrideDurationInMinutes()}
                                            />
                                            <br />
                                        </Fragment>
                                    )}

                                    {step === ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                                        <StartScreen
                                            createdAssessment={
                                                createdAssessment
                                            }
                                            assessment={assessment}
                                            ongoing={generalOngoing}
                                            myDomain={myDomain}
                                            ssoEnabled={ssoEnabled}
                                        />
                                    )}
                                </div>
                                {step !== ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                                    <KeyBehaviourCoverage
                                        questionsSelected={
                                            assessment.questionsSelected
                                        }
                                        maxByAreaAndBehaviourForSelectedQuestions={
                                            assessment.maxByAreaAndBehaviourForSelectedQuestions
                                        }
                                        maxByAreaAndBehaviourForAllQuestions={
                                            maxByAreaAndBehaviourForAllQuestions
                                        }
                                        visibility={
                                            step ===
                                            ASSESSMENT_INSIGHT_WIZARD_STEPS.CONTENT
                                        }
                                        behaviours={threatAreaDefinitions}
                                    />
                                )}
                            </div>
                            {step !== ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                                <div className={'training-wizard-footer'}>
                                    <div className={'actions'}>
                                        {step !==
                                            ASSESSMENT_INSIGHT_WIZARD_STEPS.GOAL &&
                                            step !==
                                                ASSESSMENT_INSIGHT_WIZARD_STEPS.START && (
                                                <span
                                                    className="previous-btn"
                                                    onClick={prevStep}
                                                >
                                                    Previous
                                                </span>
                                            )}
                                            <ThemedLinkButton
                                                mode="primary"
                                                continue
                                                disabled={
                                                    (!allowedSteps.includes(
                                                            assessmentInsightStore.nextStepNumber
                                                        ) &&
                                                        step !==
                                                            ASSESSMENT_INSIGHT_WIZARD_STEPS.FINALIZE) ||
                                                    (assessment.hasUnavailableQuestions &&
                                                        step ===
                                                            ASSESSMENT_INSIGHT_WIZARD_STEPS.FINALIZE)
                                                }
                                                onClick={nextStep}
                                                text={
                                                    step ===
                                                    ASSESSMENT_INSIGHT_WIZARD_STEPS.FINALIZE
                                                        ? assessment.id
                                                            ? assessment.status ===
                                                              STATUS.DRAFT
                                                                ? 'Activate'
                                                                : 'Update'
                                                            : 'Create'
                                                        : 'Continue'
                                                }
                                            ></ThemedLinkButton>
                                        </div>
                                </div>
                            )}
                            <AddScenariosModal
                                isOpen={questionsModal}
                                onRequestClose={hideQuestionsModal}
                                area={area}
                                setArea={setArea}
                                behaviour={behaviour}
                                setBehaviour={setBehaviour}
                                questions={questions}
                                threatAreaDefinitions={threatAreaDefinitions}
                                selection={assessment.selectedQuestionsIds}
                                onQuestionSelected={toggleQuestionSelected}
                                showPreviewQuestionsModal={
                                    showPreviewQuestionsModal
                                }
                            />
                            <Modal
                                isOpen={previewQuestionsModal}
                                onRequestClose={hidePreviewQuestionsModal}
                                className="preview-questions-modal"
                            >
                                <PreviewQuestion
                                    questionId={previewedQuestion}
                                    onRequestClose={hidePreviewQuestionsModal}
                                    hasMoreQuestions={hasMoreQuestions}
                                    companyId={companyId}
                                    questionsSelected={assessment.questionsSelected.map(
                                        (x) => x.id
                                    )}
                                    isWizard
                                    redirectToUpgrade={redirectToUpgrade}
                                />
                            </Modal>

                            <Modal
                                isOpen={nameChangeModal}
                                onRequestClose={hideNameChangeModal}
                                className="Modal schedule not-wide"
                            >
                                <ChangeName
                                    name={assessment.name}
                                    onRequestClose={hideNameChangeModal}
                                    nextStep={nextStep}
                                    onChangeName={onChangeName}
                                    isDraft={isDraft}
                                    redirectLink={redirectLink}
                                    saveChanges={saveChanges}
                                    itemString={name}
                                    isNameUntitled={isNameUntitled(
                                        assessment.name,
                                        'assessment_insight'
                                    )}
                                />
                            </Modal>
                            <Modal
                                isOpen={durationOverrideModal}
                                onRequestClose={hideDurationOverrideModal}
                                className="Modal schedule not-wide"
                            >
                                <ChangeDuration
                                    duration={
                                        getOverrideDurationInMinutes() ||
                                        assessment.selectedQuestionsDuration
                                    }
                                    onRequestClose={hideDurationOverrideModal}
                                    saveChanges={onChangeDuration}
                                    isDraft={isDraft}
                                />
                            </Modal>
                            <Modal
                                isOpen={saveChangesModal}
                                onRequestClose={hideSaveChangesModal}
                                className="Modal schedule not-wide"
                            >
                                <SaveChanges
                                    onRequestClose={hideSaveChangesModal}
                                    initiateSavingProgress={
                                        initiateSavingProgress
                                    }
                                    back={back}
                                    saveButtonAvailable={saveButtonAvailable}
                                    parentPath={parentRoute}
                                    editing={
                                        assessment.status === STATUS.ACTIVE ||
                                        assessment.status === STATUS.DRAFT
                                    }
                                />
                            </Modal>
                        </div>
                    ) : (
                        <Fragment>
                            <br />
                            <br />
                            <br />
                            <ErrorMessage>
                                <Alert
                                    color="warning"
                                    className="header"
                                    isOpen={true}
                                >
                                    No license available
                                </Alert>
                            </ErrorMessage>
                        </Fragment>
                    )}
                </Fragment>
            )}
        </Fragment>
    );
}

export default observer(AssessmentInsightCreate);
